// ============================================
// closeRealtimeConnection — teardown side-effects
// ============================================
// Wave 5a — Phase 1 of endSession() extracted into a single helper.
//
// Behavior preservation:
// - Awaits the MediaRecorder onstop (or 2s safety timeout) BEFORE
//   closing the AudioContext, exactly like the inline code. This is
//   load-bearing: closing AudioContext first kills the recorder's
//   source stream and the blob never gets written.
// - Each branch is gated identically to the original (`if (ref.current)`
//   for closables, `&& state !== 'inactive'` for the recorder).
// - Sets refs to null in the same order. This matters because the
//   `data-channel-message` handler (still inline) checks
//   `dataChannelRef.current?.readyState === 'open'`, so nulling here
//   guarantees no callback can run after teardown begins.
//
// Out of scope (caller still owns):
// - sessionCompletedRef / postCallInProgressRef / typingSound flags
//   (set BEFORE this helper runs).
// - Pending-timer clears (greetingRetryTimeoutRef, responseStallTimerRef,
//   idleTimeoutRef) — kept inline for now; they'll be migrated when the
//   matching start-side timers move into a `useVoiceSession` hook.
// - UI state writes (setIsSessionActive/setIsSpeaking/setIsMuted) —
//   the original code performs them AFTER teardown so they stay in
//   the caller, preserving render order.

import type { MutableRefObject } from 'react'

export interface CloseRealtimeConnectionRefs {
  mediaRecorderRef: MutableRefObject<MediaRecorder | null>
  recordedAudioBlobRef: MutableRefObject<Blob | null>
  audioContextRef: MutableRefObject<AudioContext | null>
  dataChannelRef: MutableRefObject<RTCDataChannel | null>
  peerConnectionRef: MutableRefObject<RTCPeerConnection | null>
  audioElementRef: MutableRefObject<HTMLAudioElement | null>
}

export const closeRealtimeConnection = async (
  refs: CloseRealtimeConnectionRefs,
): Promise<void> => {
  const {
    mediaRecorderRef,
    recordedAudioBlobRef,
    audioContextRef,
    dataChannelRef,
    peerConnectionRef,
    audioElementRef,
  } = refs

  // CRITICAL: stop MediaRecorder FIRST, then close AudioContext.
  if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
    const recorderStopPromise = new Promise<void>((resolve) => {
      const recorder = mediaRecorderRef.current!
      const originalOnStop = recorder.onstop
      recorder.onstop = (event) => {
        if (originalOnStop) {
          originalOnStop.call(recorder, event)
        }
        resolve()
      }
      // Safety timeout — don't block forever if onstop never fires
      setTimeout(resolve, 2000)
    })
    mediaRecorderRef.current.stop()
    console.log('🎙️ Recording stop requested — waiting for blob...')
    await recorderStopPromise
    console.log(
      '🎙️ Recording finalized:',
      recordedAudioBlobRef.current
        ? `${(recordedAudioBlobRef.current.size / (1024 * 1024)).toFixed(2)} MB`
        : 'no blob',
    )
  }

  // NOW safe to close AudioContext.
  if (audioContextRef.current) {
    try {
      audioContextRef.current.close()
      console.log('🔊 AudioContext closed')
    } catch (err) {
      console.warn('⚠️ Failed to close AudioContext:', err)
    }
    audioContextRef.current = null
  }

  // Close data channel
  if (dataChannelRef.current) {
    dataChannelRef.current.close()
    dataChannelRef.current = null
  }

  // Close peer connection (stop tracks first to release the mic).
  if (peerConnectionRef.current) {
    peerConnectionRef.current.getSenders().forEach((sender) => {
      sender.track?.stop()
    })
    peerConnectionRef.current.close()
    peerConnectionRef.current = null
  }

  // Remove audio element
  if (audioElementRef.current) {
    audioElementRef.current.pause()
    audioElementRef.current.srcObject = null
    audioElementRef.current.remove()
    audioElementRef.current = null
  }
}
